import { skyFlightFrame } from '../../../packages/domain/src/92-sky-flight-frame.mjs';
import {
  skyRigidAssemblyDescriptors,
  SKY_RIGID_ASSEMBLY_LIMITS,
} from '../../../packages/domain/src/103-sky-rigid-assembly.mjs';

/** Extract named, opaque rigid GLTF surfaces without modifying their geometry.
 * Each output triangle is in its declared joint's local metre frame, including
 * intermediate mesh transforms. Root/joint frames must be rigid, and the actual
 * scene hierarchy must agree with the schema. Skinning/morphs require another
 * explicit owner and are rejected here. Asset-specific hashes and readiness
 * authorization remain with the caller; malformed artwork throws a load error. */
export function extractSkyRigidGeometry(T, scene, { rootName, parts: sourceParts } = {}) {
  const descriptors = skyRigidAssemblyDescriptors(sourceParts);
  if (!scene?.isObject3D || !descriptors || typeof rootName !== 'string' || !rootName)
    throw new Error('Rigid collision requires actual artwork and a valid named assembly.');
  scene.updateWorldMatrix(true, true);
  const nodes = new Map();
  scene.traverse((node) => {
    if (!nodes.has(node.name)) nodes.set(node.name, []);
    nodes.get(node.name).push(node);
  });
  const named = (name) => {
    const matches = nodes.get(name);
    if (matches?.length !== 1) throw new Error(`Ambiguous rigid collision node: ${name}`);
    return matches[0];
  };
  const frame = (matrix) => {
    const e = matrix.elements;
    const result = skyFlightFrame({
      origin: { x: e[12], y: e[13], z: e[14] },
      right: { x: e[0], y: e[1], z: e[2] },
      up: { x: e[4], y: e[5], z: e[6] },
      back: { x: e[8], y: e[9], z: e[10] },
    });
    if (!result || e[3] !== 0 || e[7] !== 0 || e[11] !== 0 || e[15] !== 1)
      throw new Error('Collision joint must be a rigid metre-scale frame.');
    return result;
  };
  const root = named(rootName);
  frame(root.matrixWorld);
  const joints = new Map(descriptors.map((part) => [part.id, named(part.node)]));
  const jointNodes = new Set(joints.values());
  const descendant = (node, parent) => {
    while (node && node !== parent) node = node.parent;
    return node === parent;
  };
  let total = 0;
  const claimedMeshes = new Set();
  return descriptors.map((descriptor) => {
    const joint = joints.get(descriptor.id),
      surface = named(descriptor.mesh);
    const parent = descriptor.parentId ? joints.get(descriptor.parentId) : root;
    if (!descendant(joint, parent) || !descendant(surface, joint))
      throw new Error('Rigid collision hierarchy does not match its declared joint ownership.');
    for (let ancestor = joint.parent; ancestor && ancestor !== parent; ancestor = ancestor.parent)
      if (jointNodes.has(ancestor))
        throw new Error('Rigid collision cannot skip a declared parent joint.');
    const rest = parent.matrixWorld.clone().invert().multiply(joint.matrixWorld);
    const inverse = joint.matrixWorld.clone().invert();
    const triangles = [];
    surface.traverse((mesh) => {
      if (mesh !== joint && jointNodes.has(mesh))
        throw new Error('Rigid collision surface cannot include another articulated joint.');
      if (!mesh.isMesh) return;
      if (claimedMeshes.has(mesh)) throw new Error('Rigid collision surface has multiple owners.');
      claimedMeshes.add(mesh);
      if (mesh.isSkinnedMesh || mesh.geometry.morphAttributes.position?.length)
        throw new Error('Rigid armor cannot contain unowned deformation.');
      const geometry = mesh.geometry,
        positions = geometry.attributes.position,
        index = geometry.index;
      const count = index?.count ?? positions?.count;
      if (
        !positions ||
        positions.itemSize !== 3 ||
        !Number.isSafeInteger(count) ||
        count <= 0 ||
        count % 3 !== 0 ||
        positions.count > SKY_RIGID_ASSEMBLY_LIMITS.triangles * 3 ||
        triangles.length + count / 3 > SKY_RIGID_ASSEMBLY_LIMITS.trianglesPerPart ||
        (total += count / 3) > SKY_RIGID_ASSEMBLY_LIMITS.triangles
      )
        throw new Error('Rigid armor must contain bounded complete triangles.');
      const transform = inverse.clone().multiply(mesh.matrixWorld);
      frame(transform);
      const vertices = Array.from({ length: positions.count }, (_, i) => {
        const point = new T.Vector3().fromBufferAttribute(positions, i).applyMatrix4(transform);
        if (![point.x, point.y, point.z].every(Number.isFinite))
          throw new Error('Rigid armor contains a nonfinite vertex.');
        return { x: point.x, y: point.y, z: point.z };
      });
      for (let i = 0; i < count; i += 3) {
        const [a, b, c] = [0, 1, 2].map((offset) => {
          const at = index ? index.getX(i + offset) : i + offset;
          if (!Number.isSafeInteger(at) || at < 0 || at >= vertices.length)
            throw new Error('Rigid armor contains an invalid triangle index.');
          return vertices[at];
        });
        triangles.push({ a, b, c });
      }
    });
    if (!triangles.length) throw new Error('Rigid collision surface contains no triangles.');
    return { id: descriptor.id, parentId: descriptor.parentId, restFrame: frame(rest), triangles };
  });
}
